import { useCallback, useEffect, useState } from "react";
import { ApiError } from "./api.js";

/**
 * Fetch-on-mount with the three states a screen actually has to render.
 *
 * Deliberately not TanStack Query, which §2 of docs/PLAN.md named. Query earns its place when the
 * app needs optimistic mutations with rollback — and in this app that job belongs to the outbox
 * (apps/web/src/rate/outbox.ts), which is durable across reloads and retries with backoff because
 * losing a sitting of 200 ratings is the one failure this app cannot have. Adding Query now would
 * mean two systems for server state and the rate loop would use neither. Recorded in PLAN.md §2.
 */

export interface Resource<T> {
  data: T | null;
  /** True until the first response, and again on an explicit reload. */
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are the caller's contract
  const run = useCallback(load, deps);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);

    run()
      .then((result) => {
        // A response that arrives after the screen moved on must not paint over the new one.
        if (live) setData(result);
      })
      .catch((cause) => {
        if (!live) return;
        // A 401 is already broadcast by the api layer; the shell handles it, not every screen.
        setError(cause instanceof ApiError ? cause : new ApiError(0, "error", String(cause)));
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [run, nonce]);

  return { data, loading, error, reload: useCallback(() => setNonce((n) => n + 1), []) };
}
